import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import AccessTokenManager from '../services/accessTokenManager';

const ProfileButton: React.FC = () => {
  const { user, logout, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  // Get user role from JWT token
  useEffect(() => {
    const getUserRole = async () => {
      if (isAuthenticated) {
        try {
          // Get the access token from Auth0 (waits for it to be available)
          const token = await getAccessTokenSilently();
          if (token) {
            // Decode JWT token (it's base64 encoded)
            const payload = JSON.parse(atob(token.split('.')[1]));
            // Auth0 uses namespaced claims for custom fields
            const role = payload['https://app.fxer.ai/role'];
            if (role) {
              setUserRole(role);
            }
          }
        } catch (error) {
          console.error('Error getting or decoding token:', error);
        }
      }
    };

    getUserRole();
  }, [isAuthenticated, user, getAccessTokenSilently]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center p-2 rounded-full bg-gray-100 hover:bg-gray-200"
      >
        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
          {user?.name ? user.name.charAt(0) : 'U'}
        </div>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
          <div className="px-4 py-2">
            <div className="text-sm font-medium text-gray-900">{user?.name}</div>
            <div className="text-sm text-gray-500">{user?.email}</div>
            {userRole === 'ADMIN' && (
              <div className="mt-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                  ADMIN
                </span>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100"></div>

          {userRole === 'ADMIN' && (
            <button
              onClick={() => {
                navigate('/admin');
                setIsOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Admin Panel
            </button>
          )}

          <button
            onClick={() => {
              navigate('/profile');
              setIsOpen(false);
            }}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Profile
          </button>

          <a
            href="https://billing.stripe.com/p/login/00gbLocif2OY6HK8ww"
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Manage Subscription
          </a>
          
          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
          >
            Log Out
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileButton;